<template>
  <div class="search-control-group">
    <div class="search-control">
      <i class="fas fa-search"></i>
      <input
        ref="searchInput"
        :value="value"
        type="text"
        :placeholder="$t('retailSale.search.placeholder')"
        @input="$emit('input', $event.target.value)"
        @keyup.enter="submitInput"
      />
      <kbd>F9</kbd>
      <button type="button" @click="clearSearch">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <button type="button" class="search-submit" @click="submitSearch">
      <i class="fas fa-search"></i>
      {{ $t('retailSale.search.button') }}
      <i class="fas fa-level-down-alt" aria-hidden="true"></i>
    </button>

    <div class="scanner-status" :class="`scanner-status--${scanStatusType}`" role="status" aria-live="polite">
      <span class="scanner-status-dot"></span>
      <span class="scanner-status-text" :title="scanStatusText">{{ scanStatusText }}</span>
      <button
        v-if="feedback && feedback.type === 'error' && !pendingCount"
        type="button"
        class="scanner-status-dismiss"
        :aria-label="$t('retailSale.search.dismissStatus')"
        @click="$emit('dismiss-scan-feedback')"
      ><i class="fas fa-times" aria-hidden="true"></i></button>
      <i v-else class="fas fa-barcode" aria-hidden="true"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RetailSaleSearchControl',
  props: {
    value: {
      type: String,
      default: ''
    },
    scanEnabled: {
      type: Boolean,
      default: true
    },
    pendingCount: {
      type: Number,
      default: 0
    },
    feedback: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      scanBuffer: '',
      lastScanKeyAt: 0,
      scanOverflow: false
    }
  },
  computed: {
    scanStatusType() {
      if (this.pendingCount) return 'pending'
      if (this.feedback) return this.feedback.type
      return this.scanEnabled ? 'ready' : 'paused'
    },
    scanStatusText() {
      if (this.pendingCount) return this.$t('retailSale.search.scanningPending', { count: this.pendingCount })
      if (this.feedback) return this.$t(this.feedback.key, this.feedback.params || {})
      return this.$t(this.scanEnabled ? 'retailSale.search.readyToScan' : 'retailSale.search.scanPaused')
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleShortcut)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleShortcut)
  },
  methods: {
    handleShortcut(event) {
      if (!this.scanEnabled) return

      if (event.key === 'F9') {
        event.preventDefault()
        this.$refs.searchInput.focus()
        this.$refs.searchInput.select()
        return
      }

      if (event.ctrlKey || event.altKey || event.metaKey) return
      const target = event.target
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return

      const now = Date.now()
      if (event.key === 'Enter') {
        if ((this.scanBuffer || this.scanOverflow) && now - this.lastScanKeyAt <= 120) {
          event.preventDefault()
          this.$emit('scan', this.scanOverflow ? `${this.scanBuffer}...` : this.scanBuffer)
        }
        this.scanBuffer = ''
        this.scanOverflow = false
        return
      }

      if (event.key.length !== 1) {
        this.scanBuffer = ''
        this.scanOverflow = false
        return
      }

      const inSequence = now - this.lastScanKeyAt <= 120
      if (!inSequence) this.scanOverflow = false
      this.scanBuffer = inSequence
        ? `${this.scanBuffer}${event.key}`
        : event.key
      this.lastScanKeyAt = now
      if (this.scanBuffer.length > 64) {
        this.scanOverflow = true
        this.scanBuffer = this.scanBuffer.slice(0, 64)
      }
    },
    submitInput(event) {
      const value = event.target.value.trim()
      if (/^\d+$/.test(value) || value.length > 13) {
        this.$emit('scan', value)
        return
      }

      this.$emit('search', value)
    },
    submitSearch() {
      this.$emit('search', this.value)
    },
    clearSearch() {
      this.$emit('input', '')
      this.$emit('clear')
    }
  }
}
</script>

<style scoped src="../../styles/search-control.css"></style>
